import { useEffect } from "react";
import { LIFECYCLE, STATUS } from "react-joyride";
import { TOUR_SPOTLIGHT_SYNC_MS } from "../constants/tour";

const readTargetRect = (target) => {
   const element = typeof target === "string" ? document.querySelector(target) : target;

   if (!element) {
      return null;
   }

   const { top, left, width, height } = element.getBoundingClientRect();

   return { top, left, width, height };
};

// Rounded to match how the library stores the rect, so sub-pixel noise is not a move
const hasTargetMoved = ({ from, to }) =>
   Math.round(from.top) !== Math.round(to.top) ||
   Math.round(from.left) !== Math.round(to.left) ||
   Math.round(from.width) !== Math.round(to.width) ||
   Math.round(from.height) !== Math.round(to.height);

// Window scroll is one of the events the library recomputes the cutout on, and
// dispatching it is the only lever it exposes for a move it cannot see itself
const requestSpotlightRecompute = () => window.dispatchEvent(new Event("scroll"));

// The library keeps the cutout on the target through window resizes and changes
// to the target's own content box, but not through a move it makes no event for:
// a sibling reflowing, a webfont swapping in, or content above it growing. The
// tooltip tracks all of those, so a stale cutout leaves the highlight sitting
// away from the element the tooltip is still pointing at.
const useTourSpotlightSync = ({ step, state }) => {
   const target = step?.spotlightTarget ?? step?.target ?? null;
   const isStopOnScreen = state.status === STATUS.RUNNING && state.lifecycle === LIFECYCLE.TOOLTIP;

   useEffect(() => {
      if (!isStopOnScreen || !target) {
         return undefined;
      }

      let frameId = null;
      // Seeded here rather than on the first frame, because this is the moment the
      // library measured the target and a shift can land before that frame runs
      let syncedRect = readTargetRect(target);
      let lastSyncAt = 0;

      const watch = (now) => {
         const rect = readTargetRect(target);

         if (!syncedRect) {
            syncedRect = rect;
         } else if (
            rect &&
            hasTargetMoved({ from: syncedRect, to: rect }) &&
            now - lastSyncAt >= TOUR_SPOTLIGHT_SYNC_MS
         ) {
            requestSpotlightRecompute();
            syncedRect = rect;
            lastSyncAt = now;
         }

         frameId = requestAnimationFrame(watch);
      };

      frameId = requestAnimationFrame(watch);

      return () => cancelAnimationFrame(frameId);
   }, [isStopOnScreen, target]);
};

export default useTourSpotlightSync;
